import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, FileCheck2, Scale } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { fmtDate, OCC_STATUS } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ReasonsList } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";

function OccurrenceCard({ occ, me, users, onChanged }) {
  const [dialog, setDialog] = useState(null); // evidencia | exonerar | reprogramar
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState(null);
  const ob = occ.obligation || {};

  const act = async (action, payload = {}) => {
    setErrors(null);
    try {
      await api.post(`/legal/occurrences/${occ.id}/action`, { action, ...payload });
      toast.success("Acción ejecutada");
      setDialog(null); setForm({});
      onChanged?.();
    } catch (e) {
      const reasons = e?.response?.data?.detail?.reasons;
      if (reasons) setErrors(reasons);
      else toast.error(apiError(e));
    }
  };

  const canAct = !me?.roles?.includes("VIEWER");
  const open = ["programada", "proxima", "pendiente_evidencia", "vencida", "rechazada"].includes(occ.status);

  return (
    <div className="ts-card p-4" data-testid={`occurrence-${occ.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Chip map={OCC_STATUS} value={occ.status} testid={`occ-status-${occ.id}`} />
            <p className="font-medium text-slate-900">{occ.title}</p>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Vence: <strong className={occ.status === "vencida" ? "text-red-600" : ""}>{fmtDate(occ.due_date)}</strong>
            {" · "}Responsable: <strong>{userName(users, ob.internal_owner)}</strong>
            {" · "}Revisor: <strong>{userName(users, ob.reviewer)}</strong>
            {" · "}Fuente: {ob.source_clause} (pág. {ob.source_page})
          </p>
          {occ.contract && (
            <Link to={`/legal/contratos/${occ.contract_id}?tab=obligaciones`} className="mt-0.5 block text-xs text-blue-700 hover:underline" data-testid={`occ-contract-${occ.id}`}>
              {occ.contract.contract_number} — {occ.contract.title}
            </Link>
          )}
          {occ.evidence_required && <p className="mt-1 text-xs text-amber-700">Requiere evidencia ({ob.evidence_type}) · Criterio: {ob.acceptance_criteria}</p>}
          {occ.completion_note && <p className="mt-1 text-xs text-slate-500">Nota: {occ.completion_note}</p>}
        </div>
        {canAct && open && (
          <div className="flex flex-wrap gap-1.5">
            {occ.evidence_required && (
              <Button size="sm" variant="outline" onClick={() => { setForm({}); setErrors(null); setDialog("evidencia"); }} data-testid={`occ-evidence-${occ.id}`}>
                <FileCheck2 className="mr-1 h-3.5 w-3.5" /> Aportar evidencia
              </Button>
            )}
            <Button size="sm" onClick={() => act("confirmar_cumplimiento", { note: form.note })} data-testid={`occ-confirm-${occ.id}`}>
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Confirmar cumplimiento
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setForm({}); setErrors(null); setDialog("exonerar"); }} data-testid={`occ-exonerate-${occ.id}`}>Solicitar exoneración</Button>
            <Button size="sm" variant="ghost" onClick={() => { setForm({}); setErrors(null); setDialog("reprogramar"); }} data-testid={`occ-reprogram-${occ.id}`}>Reprogramar</Button>
            {occ.status === "vencida" && <Button size="sm" variant="destructive" onClick={() => act("escalar")} data-testid={`occ-escalate-${occ.id}`}>Escalar</Button>}
          </div>
        )}
      </div>
      {errors && <div className="mt-3"><ReasonsList reasons={errors} /></div>}

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="occ-action-dialog">
          <DialogHeader>
            <DialogTitle>{dialog === "evidencia" ? "Aportar evidencia" : dialog === "exonerar" ? "Solicitar exoneración" : "Reprogramar"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {dialog === "evidencia" && (
              <>
                <div><Label>Título de la evidencia</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={occ.title} data-testid="occ-evidence-title" /></div>
                <div><Label>Descripción / referencia</Label><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="occ-evidence-desc" /></div>
                <Button className="w-full" onClick={() => act("aportar_evidencia", { title: form.title, description: form.description })} data-testid="occ-evidence-submit">Aportar y enviar a revisión</Button>
              </>
            )}
            {dialog === "exonerar" && (
              <>
                <div><Label>Motivo *</Label><Textarea value={form.motivo || ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} data-testid="occ-exonerate-motivo" /></div>
                <div>
                  <Label>Aprobador *</Label>
                  <Select value={form.approver_id || ""} onValueChange={(v) => setForm({ ...form, approver_id: v })}>
                    <SelectTrigger data-testid="occ-exonerate-approver"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="usr_02">Elena Torres — Managing Director</SelectItem>
                      <SelectItem value="usr_03">Carlos Vega — Legal Director</SelectItem>
                      <SelectItem value="usr_05">Javier Ruiz — Finance Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Consecuencia</Label><Input value={form.consecuencia || ""} onChange={(e) => setForm({ ...form, consecuencia: e.target.value })} data-testid="occ-exonerate-conseq" /></div>
                <Button className="w-full" disabled={!form.motivo?.trim() || !form.approver_id} onClick={() => act("exonerar", form)} data-testid="occ-exonerate-submit">Solicitar exoneración</Button>
              </>
            )}
            {dialog === "reprogramar" && (
              <>
                <div><Label>Nueva fecha</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="occ-reprogram-date" /></div>
                <Button className="w-full" disabled={!form.due_date} onClick={() => act("reprogramar", { due_date: new Date(form.due_date).toISOString() })} data-testid="occ-reprogram-submit">Reprogramar</Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Obligaciones() {
  const [params, setParams] = useSearchParams();
  const estado = params.get("estado") || "";
  const riesgo = params.get("riesgo") || "";
  const { user } = useAuth();
  const { map } = useUsers();
  const qc = useQueryClient();

  const { data: occs, isLoading, refetch } = useQuery({
    queryKey: ["occurrences", estado],
    queryFn: async () => (await api.get("/legal/occurrences", { params: { status: estado || undefined } })).data,
  });
  const { data: defs } = useQuery({
    queryKey: ["obligations-defs", riesgo],
    queryFn: async () => (await api.get("/legal/obligations", { params: { risk: riesgo || undefined } })).data,
  });

  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["legal-dashboard"] }); };
  const pendingValidation = (defs || []).filter((d) => d.status === "candidata");

  const validateDef = async (id) => {
    try {
      await api.post(`/legal/obligations/${id}/validate`);
      toast.success("Obligación validada y activada");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="obligaciones-page">
      <PageHeader title="Obligaciones contractuales" subtitle="Cada cumplimiento exige evidencia aceptada cuando corresponde. Las obligaciones periódicas generan la siguiente occurrence automáticamente." />
      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={estado} onValueChange={(v) => setParams({ estado: v === "todas" ? "" : v })}>
          <SelectTrigger className="h-8 w-56" data-testid="occ-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="todas">Todas</SelectItem>{Object.entries(OCC_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {pendingValidation.length > 0 && !estado && (
        <div className="mb-5" data-testid="obligations-candidates">
          <p className="ts-caption mb-2">Obligaciones candidatas extraídas por IA — pendientes de validación ({pendingValidation.length})</p>
          <div className="space-y-2">
            {pendingValidation.slice(0, 8).map((ob) => (
              <div key={ob.id} className="ts-card flex flex-wrap items-center gap-2 p-3" data-testid={`obligation-candidate-${ob.id}`}>
                <Scale className="h-4 w-4 text-violet-500" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{ob.title}</span>
                <span className="text-xs text-slate-400">{ob.contract?.contract_number} · {ob.source_clause}</span>
                <Button size="sm" variant="outline" onClick={() => validateDef(ob.id)} data-testid={`validate-obligation-${ob.id}`}>Validar y activar</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando obligaciones…</p>
      ) : !occs?.length ? (
        <EmptyState title="Sin occurrences" description="Las occurrences se generan al activar contratos con obligaciones." testid="occurrences-empty" />
      ) : (
        <div className="space-y-3">
          {occs.map((o) => <OccurrenceCard key={o.id} occ={o} me={user} users={map} onChanged={refresh} />)}
        </div>
      )}
    </div>
  );
}
